'use client';

import dynamic from 'next/dynamic';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Circle, Loader2 } from 'lucide-react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExtractForm } from '@/components/ExtractForm';
import { PageSection, SurfacePanel } from '@/components/site/page-primitives';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useWebSocket } from '@/hooks/useWebSocket';
import { isScreenshotLoadingState, shouldShowExtractionStatusPanel } from '@/lib/extractionStatus';
import { toUserFacingExtractionErrorMessage } from '@/lib/extractionErrorMessage';
import {
  buildHomepageExtractionProgress,
  type HomepageExtractionProgressModel,
} from '@/lib/homepageExtractionProgress';
import { useToast } from '@/hooks/use-toast';
import { fetchRunStatus, issueWebsocketToken, isTerminalExtractStatus, type ExtractRunSession } from '@/services/api';

const ResultDisplay = dynamic(
  () => import('@/components/ResultDisplay').then((module) => module.ResultDisplay),
  {
    ssr: false,
    loading: () => (
      <div className="p-6 text-sm text-[var(--site-ink-muted)]">
        Загружаем результат...
      </div>
    ),
  },
);

type ScraperExperienceProps = {
  children: ReactNode;
  turnstileSiteKey?: string | null;
  turnstileBypass?: boolean;
};

type ScraperExperienceContextValue = {
  errorMessage: ReactNode;
  extractionComplete: boolean;
  handleExtractFailed: (message?: string) => void;
  handleExtractStart: (run: ExtractRunSession | null) => void;
  isPending: boolean;
  isProcessing: boolean;
  isScreenshotLoading: boolean;
  json: unknown;
  manualError: string | null;
  progressModel: HomepageExtractionProgressModel | null;
  resultErrorMessage: string | null;
  screenshotUrl: string | null;
  shouldShowStatusPanel: boolean;
  turnstileBypass?: boolean;
  turnstileSiteKey?: string | null;
};

const ScraperExperienceContext = createContext<ScraperExperienceContextValue | null>(null);

function useScraperExperience() {
  const context = useContext(ScraperExperienceContext);
  if (!context) {
    throw new Error('useScraperExperience must be used within ScraperExperience');
  }
  return context;
}

export function ScraperExperience({
  children,
  turnstileSiteKey,
  turnstileBypass,
}: ScraperExperienceProps) {
  const [runSession, setRunSession] = useState<ExtractRunSession | null>(null);
  const [isPending, setIsPending] = useState(false);
  const { isLoading, screenshot_url, json, messages, status, statusMessage, error } = useWebSocket(runSession);
  const [isMounted, setIsMounted] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(true);
  const [manualError, setManualError] = useState<string | null>(null);
  const [waitingForResults, setWaitingForResults] = useState(false);
  const [currentRunQuery, setCurrentRunQuery] = useState<string | null>(null);
  const lastErrorToastRef = useRef<string | null>(null);
  const { toast } = useToast();

  const showExtractionErrorToast = useCallback((message: string) => {
    const normalizedMessage = toUserFacingExtractionErrorMessage(message);
    if (lastErrorToastRef.current === normalizedMessage) {
      return;
    }

    lastErrorToastRef.current = normalizedMessage;
    toast({
      variant: 'destructive',
      title: 'Не удалось извлечь данные',
      description: normalizedMessage,
    });
  }, [toast]);

  const updateRunQuery = useCallback((runId: string | null) => {
    if (typeof window === 'undefined') {
      return;
    }

    const nextUrl = new URL(window.location.href);
    if (runId) {
      nextUrl.searchParams.set('run', runId);
    } else {
      nextUrl.searchParams.delete('run');
    }

    const currentUrl = new URL(window.location.href);
    if (nextUrl.search === currentUrl.search) {
      setCurrentRunQuery(nextUrl.searchParams.get('run'));
      return;
    }

    window.history.replaceState(null, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    setCurrentRunQuery(nextUrl.searchParams.get('run'));
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (runSession) {
      setExtractionComplete(false);
    }
  }, [runSession]);

  useEffect(() => {
    if (json && !isLoading) {
      setExtractionComplete(true);
    }
  }, [json, isLoading]);

  useEffect(() => {
    if (!isLoading && status === 'closed') {
      setExtractionComplete(true);
    }
  }, [isLoading, status]);

  const handleExtractFailed = useCallback((message?: string) => {
    setIsPending(false);
    setExtractionComplete(true);
    if (message) {
      setManualError(message);
    }
  }, []);

  useEffect(() => {
    if (error) {
      handleExtractFailed();
    }
  }, [error, handleExtractFailed]);

  const handleExtractStart = useCallback((newRun: ExtractRunSession | null) => {
    if (newRun === null) {
      setRunSession(null);
      setIsPending(true);
      setExtractionComplete(false);
      setManualError(null);
      setWaitingForResults(true);
      lastErrorToastRef.current = null;
      updateRunQuery(null);
      return;
    }

    setRunSession(null);
    setManualError(null);

    setTimeout(() => {
      setRunSession(newRun);
      setIsPending(false);
      updateRunQuery(newRun.runId);
    }, 50);
  }, [updateRunQuery]);

  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') {
      return;
    }

    const syncRunQuery = () => {
      const params = new URLSearchParams(window.location.search);
      setCurrentRunQuery(params.get('run'));
    };

    syncRunQuery();
    window.addEventListener('popstate', syncRunQuery);
    return () => {
      window.removeEventListener('popstate', syncRunQuery);
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const runId = currentRunQuery;
    if (!runId || runSession?.runId === runId) {
      return;
    }

    let isCurrent = true;
    setManualError(null);
    setWaitingForResults(true);
    setIsPending(false);
    lastErrorToastRef.current = null;

    const rehydrate = async () => {
      try {
        let nextRun = await fetchRunStatus(runId);
        if (isCurrent && !isTerminalExtractStatus(nextRun.status)) {
          const token = await issueWebsocketToken(runId);
          nextRun = { ...nextRun, ...token };
        }

        if (!isCurrent) {
          return;
        }

        setRunSession(nextRun);
        setWaitingForResults(!isTerminalExtractStatus(nextRun.status) && !nextRun.json && !nextRun.error);
      } catch (rehydrationError) {
        if (!isCurrent) {
          return;
        }
        const message =
          rehydrationError instanceof Error
            ? rehydrationError.message
            : 'Не удалось восстановить состояние запуска';
        const userMessage = toUserFacingExtractionErrorMessage(message);
        setRunSession(null);
        setManualError(userMessage);
        setWaitingForResults(false);
        showExtractionErrorToast(userMessage);
        updateRunQuery(null);
      }
    };

    void rehydrate();
    return () => {
      isCurrent = false;
    };
  }, [currentRunQuery, isMounted, runSession?.runId, showExtractionErrorToast, updateRunQuery]);

  const activeError = error ?? manualError;
  const progressModel = useMemo(
    () =>
      buildHomepageExtractionProgress({
        isPending,
        messages,
        runStatus: runSession?.status ?? null,
        runStep: runSession?.step ?? null,
        socketStatus: status,
        statusMessage,
        waitingForResults,
      }),
    [isPending, messages, runSession?.status, runSession?.step, status, statusMessage, waitingForResults],
  );
  const isProcessing = isLoading || isPending || status === 'connecting';
  const shouldShowStatusPanel = shouldShowExtractionStatusPanel(
    status,
    isPending,
    waitingForResults,
    Boolean(progressModel),
    error,
    manualError,
  );
  const isScreenshotLoading = isScreenshotLoadingState(isProcessing, screenshot_url);

  useEffect(() => {
    if (json || error || manualError || (!isProcessing && status === 'closed')) {
      setWaitingForResults(false);
    }
  }, [json, error, manualError, isProcessing, status]);

  useEffect(() => {
    if (!error) {
      return;
    }

    showExtractionErrorToast(error);
  }, [error, showExtractionErrorToast]);

  const resultErrorMessage = useMemo(() => {
    if (activeError == null) {
      return null;
    }

    return toUserFacingExtractionErrorMessage(activeError);
  }, [activeError]);

  const errorMessage = useMemo(() => {
    if (resultErrorMessage == null) {
      return null;
    }

    return (
      <Alert variant="destructive" data-testid="extraction-error">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{resultErrorMessage}</AlertDescription>
      </Alert>
    );
  }, [resultErrorMessage]);

  const value = useMemo<ScraperExperienceContextValue>(() => ({
    errorMessage,
    extractionComplete,
    handleExtractFailed,
    handleExtractStart,
    isPending,
    isProcessing,
    isScreenshotLoading,
    json,
    manualError,
    progressModel,
    resultErrorMessage,
    screenshotUrl: screenshot_url ?? null,
    shouldShowStatusPanel,
    turnstileBypass,
    turnstileSiteKey: turnstileSiteKey ?? null,
  }), [
    errorMessage,
    extractionComplete,
    handleExtractFailed,
    handleExtractStart,
    isPending,
    isProcessing,
    isScreenshotLoading,
    json,
    manualError,
    progressModel,
    resultErrorMessage,
    screenshot_url,
    shouldShowStatusPanel,
    turnstileBypass,
    turnstileSiteKey,
  ]);

  return (
    <ErrorBoundary>
      <ScraperExperienceContext.Provider value={value}>
        {children}
      </ScraperExperienceContext.Provider>
    </ErrorBoundary>
  );
}

export function ScraperExperiencePanel() {
  const {
    errorMessage,
    extractionComplete,
    handleExtractFailed,
    handleExtractStart,
    isProcessing,
    progressModel,
    shouldShowStatusPanel,
    turnstileBypass,
    turnstileSiteKey,
  } = useScraperExperience();
  const [showProgressDetails, setShowProgressDetails] = useState(false);

  useEffect(() => {
    if (progressModel?.currentStepKey === 'submit' || !shouldShowStatusPanel) {
      setShowProgressDetails(false);
    }
  }, [progressModel?.currentStepKey, shouldShowStatusPanel]);

  const currentProgressItem =
    progressModel?.items.find((item) => item.state === 'current') ??
    progressModel?.items[0] ??
    null;
  const lastCompletedProgressItem =
    progressModel?.items
      ? [...progressModel.items].reverse().find((item) => item.state === 'complete') ?? null
      : null;
  const completedProgressCount =
    progressModel?.items.filter((item) => item.state === 'complete').length ?? 0;
  const renderErrorInsideStatusPanel = Boolean(errorMessage && shouldShowStatusPanel && progressModel);
  const displayHeadline =
    renderErrorInsideStatusPanel && currentProgressItem
      ? `Ошибка на этапе: ${currentProgressItem.label}`
      : progressModel?.headline ?? null;

  return (
    <div id="live-extraction">
      <SurfacePanel className="gap-4 border-[color:rgba(255,255,255,0.62)] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(255,255,255,0.84))] p-[var(--site-home-hero-panel-pad)] shadow-[0_28px_80px_rgba(23,38,59,0.1)] max-sm:gap-4 max-sm:shadow-[0_16px_36px_rgba(23,38,59,0.08)]">
        <ExtractForm
          onExtractStart={handleExtractStart}
          onExtractFailed={handleExtractFailed}
          isLoading={isProcessing}
          extractionComplete={extractionComplete}
          turnstileSiteKeyProp={turnstileSiteKey}
          turnstileBypassProp={turnstileBypass}
        />

        {!renderErrorInsideStatusPanel ? errorMessage : null}

        {shouldShowStatusPanel && progressModel ? (
          <SurfacePanel
            tone="muted"
            className="gap-3 overflow-hidden"
            data-testid="extraction-status"
            aria-live="polite"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <p className="font-semibold text-[var(--site-ink-strong)]">
                    {displayHeadline}
                  </p>
                  <span className="inline-flex items-center rounded-full bg-[color:rgba(23,38,59,0.05)] px-2 py-0.5 text-[11px] font-medium text-[var(--site-ink-subtle)]">
                    {completedProgressCount}
                    /
                    {progressModel.items.length}
                  </span>
                </div>
                <p className="max-w-2xl text-sm leading-5 text-[var(--site-ink-muted)]">
                  {progressModel.detail}
                </p>
              </div>

              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 self-start rounded-none p-0 text-sm font-medium text-[var(--site-ink-muted)] transition-colors duration-200 hover:text-[var(--site-ink-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-primary-fill)] focus-visible:ring-offset-2"
                aria-expanded={showProgressDetails}
                aria-controls="extraction-status-details"
                onClick={() => setShowProgressDetails((current) => !current)}
              >
                {showProgressDetails ? 'Скрыть этапы' : 'Этапы'}
                {showProgressDetails ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>

            {currentProgressItem ? (
              <div
                data-testid="extraction-status-transition"
                className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[var(--site-ink-muted)]"
              >
                {lastCompletedProgressItem && lastCompletedProgressItem.key !== currentProgressItem.key ? (
                  <>
                    <span>{lastCompletedProgressItem.label}</span>
                    <span aria-hidden="true">→</span>
                  </>
                ) : null}
                <span className="font-medium text-[var(--site-ink-strong)]">
                  {currentProgressItem.label}
                </span>
              </div>
            ) : null}

            {renderErrorInsideStatusPanel ? errorMessage : null}

            {showProgressDetails ? (
              <div
                id="extraction-status-details"
                data-testid="extraction-status-details"
                className="max-h-44 overflow-y-auto border-t border-[color:rgba(23,38,59,0.08)] pt-2 pr-1"
              >
                <ol className="space-y-0">
                  {progressModel.items.map((item, index) => {
                    const isCurrent = item.state === 'current';
                    const isComplete = item.state === 'complete';
                    const markerClassName = isComplete
                      ? 'border-[var(--site-primary-fill)] bg-[var(--site-primary-fill)] text-[var(--site-primary-text)]'
                      : isCurrent
                        ? 'border-[var(--site-primary-fill)] bg-[color:rgba(23,38,59,0.06)] text-[var(--site-primary-fill)]'
                        : 'border-[color:var(--site-border-soft)] bg-white text-[var(--site-ink-subtle)]';
                    const toneClassName = isCurrent
                      ? 'text-[var(--site-ink-strong)]'
                      : isComplete
                        ? 'text-[var(--site-ink)]'
                        : 'text-[var(--site-ink-muted)]';
                    const stateLabel = isCurrent ? 'СЕЙЧАС' : isComplete ? 'ГОТОВО' : null;

                    return (
                      <li
                        key={item.key}
                        className={`grid grid-cols-[auto_1fr] items-start gap-x-3 py-2.5 ${
                          index > 0 ? 'border-t border-[color:rgba(23,38,59,0.06)]' : ''
                        }`}
                        aria-current={isCurrent ? 'step' : undefined}
                      >
                        <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border ${markerClassName}`}>
                          {isComplete ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : isCurrent ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Circle className="h-2.5 w-2.5" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className={`text-sm font-medium ${toneClassName}`}>
                              {item.label}
                            </p>
                            {stateLabel ? (
                              <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--site-ink-subtle)]">
                                {stateLabel}
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 text-xs leading-5 text-[var(--site-ink-muted)]">
                            {item.description}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ) : null}
          </SurfacePanel>
        ) : null}
      </SurfacePanel>
    </div>
  );
}

function ExtractionDataError({ message }: { message: string }) {
  return (
    <div
      className="flex min-h-[24rem] flex-col justify-center gap-4 p-6"
      data-testid="extraction-result-error"
      role="alert"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[color:rgba(185,28,28,0.08)] text-red-700">
        <AlertCircle className="h-5 w-5" />
      </div>
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-[var(--site-ink-strong)]">
          JSON не сформирован
        </h3>
        <p className="max-w-2xl text-sm leading-6 text-[var(--site-ink-muted)]">
          {message}
        </p>
      </div>
    </div>
  );
}

export function ScraperExperienceResults() {
  const { isProcessing, isScreenshotLoading, json, resultErrorMessage, screenshotUrl } = useScraperExperience();
  const shouldShowDataError = Boolean(screenshotUrl && resultErrorMessage && !json && !isProcessing);

  if (!screenshotUrl && !json && !isProcessing) {
    return null;
  }

  return (
    <PageSection className="gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">
          Результат запуска
        </h2>
      </div>

      <div className="grid gap-4 lg:grid-cols-[0.95fr_2.05fr] lg:items-start">
        <SurfacePanel className="hidden overflow-hidden p-0 lg:block">
          <ResultDisplay
            screenshotUrl={screenshotUrl}
            jsonData={null}
            isLoading={isScreenshotLoading}
            displayMode="screenshot"
          />
        </SurfacePanel>

        <SurfacePanel className="overflow-hidden p-0" data-testid="extraction-results">
          {shouldShowDataError && resultErrorMessage ? (
            <ExtractionDataError message={resultErrorMessage} />
          ) : (
            <ResultDisplay
              screenshotUrl={null}
              jsonData={json}
              isLoading={isProcessing && !json}
              displayMode="data"
            />
          )}
        </SurfacePanel>
      </div>
    </PageSection>
  );
}
